import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const MONTH_NAMES = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
                     'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const DAY_NAMES = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const navBtnStyle = {
  width: 28, height: 28, border: '1px solid #D8DDDA', background: '#fff',
  '--ch': '5px', '--stroke': '#D8DDDA', cursor: 'pointer', fontSize: 16, display: 'flex',
  alignItems: 'center', justifyContent: 'center', color: '#1C3D2E', fontWeight: 600
};

// portal — календарь поверх страницы (position: fixed у поля): в окне с прокруткой (форма расхода)
// обычный выпадающий обрезался краем окна (владелец 25.10)
function DatePickerCalendar({ value, onChange, placeholder = 'Выберите дату', minDate, maxDate, disabled, coloredDates, compact = false, portal = false }) {
  const [open, setOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (value) return new Date(value + 'T00:00:00');
    if (minDate) return new Date(minDate + 'T00:00:00');
    return new Date();
  });
  const ref = useRef(null);
  const popRef = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !(popRef.current && popRef.current.contains(e.target))) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Поверх страницы: под полем, а если снизу не помещается — над ним
  useEffect(() => {
    if (!open || !portal || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const h = 340;
    setPos({ top: r.bottom + 4 + h > window.innerHeight ? Math.max(8, r.top - 4 - h) : r.bottom + 4,
      left: Math.max(8, Math.min(r.left, window.innerWidth - 308)) });
    // Прокрутили окно или поменяли размер — календарь закрываем, иначе он «отрывается» от поля (ревью 26.10)
    const close = (e) => { if (!(popRef.current && e.target instanceof Node && popRef.current.contains(e.target))) setOpen(false); };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); };
  }, [open, portal]);

  // Escape закрывает (UX-аудит 25.10); перехват раньше Modal — окно под ним остаётся
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);

  const formatDisplay = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${d}.${m}.${y}`;
  };

  const formatValue = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const isDisabled = (date) => {
    if (!date) return true;
    if (minDate) {
      const min = new Date(minDate + 'T00:00:00');
      if (date < min) return true;
    }
    if (maxDate) {
      const max = new Date(maxDate + 'T00:00:00');
      if (date > max) return true;
    }
    return false;
  };

  const getDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let i = 1; i <= lastDay.getDate(); i++) days.push(new Date(year, month, i));
    return days;
  };

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));

  const handleSelect = (date) => {
    if (isDisabled(date)) return;
    onChange(formatValue(date));
    setOpen(false);
  };

  const todayStr = formatValue(new Date());
  const days = getDays();

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        onClick={() => !disabled && setOpen(o => !o)}
        style={{
          width: '100%',
          padding: compact ? '8px 12px' : '12px 16px',
          fontSize: compact ? 14 : 15,
          lineHeight: compact ? 1.4 : undefined,
          '--ch': compact ? '5px' : '8px', '--stroke': (compact ? '#D8DDDA' : '#D8DDDA'),
          border: compact ? '1px solid #D8DDDA' : '1px solid #D8DDDA',
          background: disabled ? '#F4F4EF' : '#fff',
          color: '#1C3D2E', boxSizing: 'border-box',
          cursor: disabled ? 'default' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          userSelect: 'none', opacity: disabled ? 0.6 : 1, fontFamily: 'inherit',
        }}
      >
        <span style={{ color: value ? '#1C3D2E' : '#5E6561' }}>{value ? formatDisplay(value) : placeholder}</span>
        <span style={{ fontSize: 11, color: '#5E6561' }}>▼</span>
      </div>
      {open && (!portal || pos) && (portal ? createPortal : (x) => x)(
        <div ref={popRef} style={{
          ...(portal ? { position: 'fixed', top: pos.top, left: pos.left, zIndex: 2000 }
            : { position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 1000 }),
          background: '#fff', '--ch': '8px', '--stroke': '#D8DDDA', padding: 14,
          border: '1px solid #D8DDDA', boxShadow: '0 4px 16px rgba(28,61,46,0.10)',
          width: 300, userSelect: 'none'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <button type="button" onClick={prevMonth} style={navBtnStyle}>‹</button>
            <span style={{ fontWeight: 600, fontSize: 14, color: '#1C3D2E' }}>
              {MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </span>
            <button type="button" onClick={nextMonth} style={navBtnStyle}>›</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
            {DAY_NAMES.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 11, color: '#5E6561', fontWeight: 500, padding: 2, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{d}</div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {days.map((date, idx) => {
              if (!date) return <div key={idx} />;
              const dateStr = formatValue(date);
              const dis = isDisabled(date);
              const selected = value === dateStr;
              const today = dateStr === todayStr;
              const isGreen = coloredDates?.green?.includes(dateStr);
              const isYellow = coloredDates?.yellow?.includes(dateStr);
              const isRed = coloredDates?.red?.includes(dateStr);
              let bg = selected ? '#1C3D2E' : 'transparent';
              let color = dis ? '#A9B1AC' : selected ? '#fff' : '#1C3D2E';
              if (!selected && !dis) {
                if (isGreen) { bg = '#DFF2E8'; color = '#1C3D2E'; }
                else if (isYellow) { bg = '#FFE7DD'; color = '#1C3D2E'; }
                else if (isRed) { bg = '#FFE7DD'; color = '#B0603C'; }
              }
              return (
                <div
                  key={idx}
                  onClick={() => !dis && handleSelect(date)}
                  style={{
                    aspectRatio: '1',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, '--ch': '5px', '--stroke': (today && !selected ? '#1C3D2E' : 'transparent'),
                    cursor: dis ? 'default' : 'pointer',
                    color,
                    background: bg,
                    fontWeight: (selected || today) ? 700 : isRed ? 600 : 400,
                    border: today && !selected ? '1px solid #1C3D2E' : '1px solid transparent',
                    transition: 'background 0.15s'
                  }}
                >
                  {date.getDate()}
                </div>
              );
            })}
          </div>
        </div>, document.body
      )}
    </div>
  );
}

export default DatePickerCalendar;
